import { useEffect } from 'react';

import { collectiveTracking, getAnalyticsUserId } from 'modules/common/components/AnalyticsTracking/utils';
import { isBotUser } from 'modules/common/integrations/Statsig/statsigUser';
import { localViews } from 'modules/Auth/AuthProvider/views';
import { FeatureGates } from 'modules/common/constants/featuregates';
import { useAccountingOnboardingProgress } from 'services/bookkeeping/queries/useAccountingOnboardingProgress';

// Last version written this page load, keyed by analytics id.
const identifiedVersions = new Map<string, string>();

/**
 * Writes the member's setup version to their analytics profile so Amplitude can split setup funnels by
 * it, including Page Viewed, which carries no event properties. The version is the one the backend
 * stamped when setup started, not the gate's current value, so it matches the progress row. Once per
 * page load, so a stale value from another device is corrected on the next visit. `enabled: false`
 * writes nothing.
 */
export default function useSetupVersionIdentify(enabled = true) {
    const { data: onboardingProgress } = useAccountingOnboardingProgress();
    const version = onboardingProgress?.setup_version;

    useEffect(() => {
        // Blank: the row predates the stamp, or the gate could not be evaluated. Nothing true to write.
        if (!enabled || !version || isBotUser()) {
            return;
        }
        collectiveTracking.onReady(() => {
            // `sendEvent` drops everything while impersonating, so recording the write would dedupe one never sent.
            if (localViews.isImpersonating()) {
                return;
            }
            const userId = getAnalyticsUserId();
            if (!userId || identifiedVersions.get(userId) === version) {
                return;
            }
            identifiedVersions.set(userId, version);
            collectiveTracking.sendEvent({
                eventType: 'identify',
                properties: { [FeatureGates.AccountingSetupRedesignV1]: version },
            });
        });
    }, [enabled, version]);
}
